import { NOYAU_COLOR_HEX, type Noyau, RAMP_NEUTRAL_HEX, palierRampStep } from '../schemas/noyau.js';
import { type ExportHeaderTheme, PALIER_HEADER_THEME } from '../schemas/palier.js';

/**
 * The three `matrix-header-theme` values, rendered as **literal** CSS for one
 * column header (issue #8).
 *
 * Literal because neither export destination can resolve a custom property:
 * the PDF is printed by Playwright on `about:blank`, which never loads
 * `styles.css`, and the HTML export is a self-contained file (decision G5)
 * that may be opened anywhere. The grid keeps its own `var()` rendering — only
 * the ramp maths is shared, in `palierRampStep`.
 *
 * Mixing is done here rather than handed to `color-mix()` for the same reason
 * the colours are literal: the HTML export is a file with an unknown viewer,
 * and a browser that does not know `color-mix` would drop the declaration and
 * render an unpainted header rather than a slightly wrong one.
 */

function rgb(hex: string): [number, number, number] {
  return [
    Number.parseInt(hex.slice(1, 3), 16),
    Number.parseInt(hex.slice(3, 5), 16),
    Number.parseInt(hex.slice(5, 7), 16),
  ];
}

/**
 * Mix `hex` into white by `percent`, in sRGB — exactly what the browser does
 * for the grid's `color-mix(in srgb, <base> <percent>%, white)`. The colour
 * space is load-bearing: `NOYAU_TEXT_FLIP` was computed with sRGB mixing, and
 * oklab shifts every mid-tone enough to invalidate it.
 */
function mixWithWhite(hex: string, percent: number): string {
  const ratio = percent / 100;
  return `#${rgb(hex)
    .map((value) => Math.round(255 + (value - 255) * ratio))
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Background and (when the theme sets one) text colour for one palier header. */
export interface HeaderThemeStyle {
  background: string;
  color?: string;
}

/**
 * Resolves one header's colours, or null when the theme paints nothing — the
 * `white` theme, a palier the `pitch` palette does not know, or
 * `noyau-gradient` on a matrix whose company noyau could not be read. Null
 * means "emit no inline style", which leaves the stylesheet's flat header.
 */
export function headerThemeStyle(
  theme: ExportHeaderTheme,
  palierNumber: number,
  noyau: Noyau | null,
): HeaderThemeStyle | null {
  if (theme === 'white') return null;

  if (theme === 'pitch') {
    const hex = PALIER_HEADER_THEME[palierNumber]?.hex;
    return hex === undefined ? null : { background: hex };
  }

  // `noyau-gradient` is the only theme that needs to know the company, so it is
  // the only one that can be asked for without the data to satisfy it.
  if (noyau === null) return null;

  const step = palierRampStep(noyau, palierNumber);
  if (step.kind === 'off-ramp') return null;
  if (step.kind === 'sabotage') {
    return { background: RAMP_NEUTRAL_HEX.sabotage.hex, color: RAMP_NEUTRAL_HEX.noir.hex };
  }
  return {
    background: mixWithWhite(NOYAU_COLOR_HEX[step.token].hex, step.percent),
    color: step.textIsBlanc ? RAMP_NEUTRAL_HEX.blanc.hex : RAMP_NEUTRAL_HEX.noir.hex,
  };
}

/** The `<th>`'s own ` style="…"` attribute — background only (empty when nothing is painted). */
export function headerCellStyleAttribute(
  theme: ExportHeaderTheme,
  palierNumber: number,
  noyau: Noyau | null,
): string {
  const style = headerThemeStyle(theme, palierNumber, noyau);
  return style === null ? '' : ` style="background:${style.background}"`;
}

/**
 * The ` style="…"` attribute for the header's *text* spans — empty unless the
 * theme sets a text colour.
 *
 * Applied to each span rather than inherited from the `<th>`, which is not a
 * nicety: `.export-col-label` carries its own `color` declaration, and a
 * class rule on the child beats an inline colour on the
 * parent. Setting it on the `<th>` alone left the `noyau-gradient` theme
 * printing black-on-#312783 at palier 6 — 2.02:1, right where
 * `NOYAU_TEXT_FLIP` exists to guarantee 4.5:1.
 *
 * `white` and `pitch` set no text colour, so their headers keep the black
 * label the stylesheet gives them.
 */
export function headerTextStyleAttribute(
  theme: ExportHeaderTheme,
  palierNumber: number,
  noyau: Noyau | null,
): string {
  const color = headerThemeStyle(theme, palierNumber, noyau)?.color;
  return color === undefined ? '' : ` style="color:${color}"`;
}
